import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Flex } from '@bonusly/design-system';

import { useUserContentDismissal } from '../../profile/user-content-dismissal';
import { useSeasonalTheme } from '../seasonal-theme-context';
// Import all wrapping paper images
import wrapping1 from './images/wrapping_1.png';
import wrapping2 from './images/wrapping_2.png';
import wrapping3 from './images/wrapping_3.png';
import wrapping4 from './images/wrapping_4.png';
import wrapping5 from './images/wrapping_5.png';
import wrapping6 from './images/wrapping_6.png';
import wrapping7 from './images/wrapping_7.png';

const WRAPPING_IMAGES = [wrapping1, wrapping2, wrapping3, wrapping4, wrapping5, wrapping6, wrapping7];

// Holiday period for wrapped presents (Dec 15, 2025 - Dec 31, 2025)
const HOLIDAY_START = new Date('2025-12-15T00:00:00Z');
const HOLIDAY_END = new Date('2025-12-31T23:59:59Z');

interface WrappedPresentProps {
	bonusId: string;
	bonusCreatedAt: string;
	isUserReceiver: boolean;
	children: React.ReactNode;
}

/**
 * Get a deterministic wrapping paper based on bonus ID
 * This ensures the same bonus always shows the same wrapping paper
 */
const getWrappingImage = (bonusId: string): string => {
	let hash = 0;
	for (let i = 0; i < bonusId.length; i++) {
		const char = bonusId.charCodeAt(i);
		hash = (hash << 5) - hash + char;
		hash = hash & hash; // Convert to 32bit integer
	}
	const index = Math.abs(hash) % WRAPPING_IMAGES.length;
	return WRAPPING_IMAGES[index];
};

/**
 * Check if a bonus was created during the holiday period
 */
const isWithinHolidayPeriod = (createdAt: string): boolean => {
	const bonusDate = new Date(createdAt);
	return bonusDate >= HOLIDAY_START && bonusDate <= HOLIDAY_END;
};

/**
 * WrappedPresent component
 *
 * Wraps recognition bonuses as holiday presents for receivers who haven't "unwrapped" them yet.
 * Uses user-content-dismissal to track unwrap state.
 *
 * Conditions for wrapping:
 * - Seasonal theme is enabled
 * - Current user is a receiver of the bonus
 * - Bonus was created between Dec 15, 2025 - Dec 31, 2025
 * - User hasn't unwrapped (dismissed) this specific bonus present
 */
export const WrappedPresent: FC<WrappedPresentProps> = ({ bonusId, bonusCreatedAt, isUserReceiver, children }) => {
	const { t } = useTranslation('components');
	const { isSeasonalThemeEnabled } = useSeasonalTheme();
	const [isUnwrapping, setIsUnwrapping] = useState(false);
	const [hasUnwrapped, setHasUnwrapped] = useState(false);

	// Pre-check conditions before making any API calls
	// This prevents network requests when wrapping is not applicable
	const shouldCheckDismissal = useMemo(() => {
		if (!isSeasonalThemeEnabled) return false;
		if (!isUserReceiver) return false;
		if (!isWithinHolidayPeriod(bonusCreatedAt)) return false;
		return true;
	}, [isSeasonalThemeEnabled, isUserReceiver, bonusCreatedAt]);

	// Get deterministic wrapping paper for this bonus
	const wrappingImage = useMemo(() => getWrappingImage(bonusId), [bonusId]);

	// Check dismissal status for this bonus present
	// Only enabled when all pre-conditions are met to avoid unnecessary API calls
	const { loading, data, updateUserContentDismissal } = useUserContentDismissal({
		name: `wrapped-present-${bonusId}`,
		namespace: 'seasonal-winter-2025',
		enabled: shouldCheckDismissal,
	});

	// Determine if this bonus should be shown wrapped
	const shouldShowWrapped = useMemo(() => {
		// Pre-conditions must be met
		if (!shouldCheckDismissal) return false;

		// Must not have been unwrapped yet
		if (loading) return true; // Show wrapped while loading to prevent flash
		if (data?.dismissed_at) return false;

		return true;
	}, [shouldCheckDismissal, loading, data]);

	const handleUnwrap = () => {
		setIsUnwrapping(true);

		// Wait for animation to complete before dismissing
		setTimeout(() => {
			updateUserContentDismissal();
			setHasUnwrapped(true);
		}, 600);
	};

	// Create unique class names based on bonus ID to prevent animation conflicts
	const uniqueId = bonusId.replace(/[^a-zA-Z0-9]/g, '');

	// Accessible labels
	const wrappedPresentLabel = t('seasonal_theme.wrapped_present.aria_label', {
		defaultValue: 'You have received a wrapped gift! Click the unwrap button to reveal your recognition.',
	});
	const unwrapButtonLabel = t('seasonal_theme.wrapped_present.unwrap_button_aria', {
		defaultValue: 'Unwrap this gift to reveal your recognition',
	});

	// If already unwrapped or shouldn't show wrapped, render children directly
	if (!shouldShowWrapped || hasUnwrapped) {
		return (
			<Box
				sx={{
					'@keyframes revealPresent': {
						'0%': { opacity: 0, transform: 'scale(0.9)' },
						'100%': { opacity: 1, transform: 'scale(1)' },
					},
				}}
				animation={hasUnwrapped ? 'revealPresent 0.4s ease-out' : undefined}
			>
				{children}
			</Box>
		);
	}

	return (
		<Box position="relative" borderRadius="lg" overflow="hidden" role="region" aria-label={wrappedPresentLabel}>
			<style>
				{`
					@keyframes unwrapPresent-${uniqueId} {
						0% {
							transform: scale(1) rotate(0deg);
							opacity: 1;
						}
						30% {
							transform: scale(1.1) rotate(-3deg);
						}
						50% {
							transform: scale(1.15) rotate(3deg);
						}
						70% {
							transform: scale(1.2) rotate(-2deg);
						}
						100% {
							transform: scale(1.5) rotate(0deg);
							opacity: 0;
						}
					}

					@keyframes shimmerPresent-${uniqueId} {
						0% {
							background-position: -200% center;
						}
						100% {
							background-position: 200% center;
						}
					}

					.wrapped-present-shimmer-${uniqueId} {
						animation: shimmerPresent-${uniqueId} 3s ease-in-out infinite;
					}
				`}
			</style>

			{/* The actual bonus content (hidden from assistive tech while wrapped) */}
			<Box visibility="hidden" aria-hidden="true">
				{children}
			</Box>

			{/* Wrapping paper overlay */}
			<Box
				position="absolute"
				top={0}
				left={0}
				right={0}
				bottom={0}
				backgroundImage={`url(${wrappingImage})`}
				backgroundSize="cover"
				backgroundPosition="center"
				borderRadius="lg"
				zIndex={1}
				role="img"
				aria-label={t('seasonal_theme.wrapped_present.wrapping_paper_aria', {
					defaultValue: 'Festive gift wrapping paper',
				})}
				style={{
					animation: isUnwrapping ? `unwrapPresent-${uniqueId} 0.6s ease-out forwards` : 'none',
				}}
			>
				{/* Subtle shimmer effect - purely decorative */}
				<Box
					className={`wrapped-present-shimmer-${uniqueId}`}
					position="absolute"
					top={0}
					left={0}
					right={0}
					bottom={0}
					background="linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%)"
					backgroundSize="200% 100%"
					borderRadius="lg"
					pointerEvents="none"
					aria-hidden="true"
				/>

				{/* Unwrap button */}
				<Flex position="absolute" bottom={4} right={4} alignItems="center" gap={2}>
					<Button
						size="md"
						bg="white"
						color="gray.800"
						fontWeight="bold"
						borderRadius="full"
						boxShadow="lg"
						px={6}
						onClick={handleUnwrap}
						isLoading={isUnwrapping}
						aria-label={unwrapButtonLabel}
						aria-busy={isUnwrapping}
						_hover={{
							bg: 'yellow.100',
							transform: 'scale(1.05)',
						}}
						_active={{
							bg: 'yellow.200',
							transform: 'scale(0.98)',
						}}
						transition="all 0.2s"
						leftIcon={
							<Box as="span" fontSize="lg" aria-hidden="true">
								🎁
							</Box>
						}
					>
						{t('seasonal_theme.wrapped_present.unwrap_button', { defaultValue: 'Unwrap me!' })}
					</Button>
				</Flex>

				{/* Gift tag decoration - purely decorative */}
				<Box
					position="absolute"
					top={4}
					left={4}
					bg="white"
					px={3}
					py={2}
					borderRadius="md"
					boxShadow="md"
					transform="rotate(-6deg)"
					fontSize="sm"
					fontWeight="medium"
					color="gray.700"
					aria-hidden="true"
				>
					<Box as="span" mr={1}>
						🎀
					</Box>
					{t('seasonal_theme.wrapped_present.gift_tag', { defaultValue: 'For you!' })}
				</Box>
			</Box>
		</Box>
	);
};

/**
 * Hook to determine if a bonus should potentially be wrapped
 * This is a lightweight check that doesn't involve dismissal state
 */
export const useShouldCheckWrapping = (bonusCreatedAt: string, isUserReceiver: boolean): boolean => {
	const { isSeasonalThemeEnabled } = useSeasonalTheme();

	return useMemo(() => {
		if (!isSeasonalThemeEnabled) return false;
		if (!isUserReceiver) return false;
		if (!isWithinHolidayPeriod(bonusCreatedAt)) return false;
		return true;
	}, [isSeasonalThemeEnabled, isUserReceiver, bonusCreatedAt]);
};
